// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs, { Dayjs } from "dayjs";
import { format, isValid } from "date-fns";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { CustomSelect } from "../../../components/CustomFields/CustomSelect";
import PhoneInput from "react-phone-number-input";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import DatePickerComponent from "../../../components/CustomFields/DatePicker";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import AddIcon from "@mui/icons-material/Add";
import StaffDocumentSave from "../../../components/CustomDocSave/StaffDocumentSave";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { GetCountryListForSelect } from "../../../services/CountryService";
import { GetSpecialityList } from "../../../services/SpecialityService";
import { GetDepartmentList } from "../../../services/DepartmentService";
import { GetCountryRegionListForSelect } from "../../../services/CountryRegionService";
import { PreferencesContext } from "../../../PreferenceContext";
import { PAGE_SIZE } from "../../../services/common/const";
import { Item } from "../../../Helpers/typings";
import CustomTooltip from "../../../components/ToolTip/CustomToolTip";
import {
  CreateTeamMember,
  GetTeamMemberDetail,
  GetTeamMemberLocationList,
  UpdateTeamMember,
} from "../../../services/TeamMemberService";
import TeamMemberDocumentSave from "../../../components/CustomDocSave/TeamMemberDocumentSave";
import {
  DeleteTeamMemberDocument,
  GetTeamMemberDocumentList,
} from "../../../services/TeamMemberDocumentService";
import { MultiUpload } from "../../../components/MultiUpload/MultiUpload";
import { dataURLtoFile } from "../../../shared/CommonFunction";
import AvailabilitySettings from "../../../components/AvailabilitySettings/AvailabilitySettings";
import TimezoneSelect, { type ITimezone } from "react-timezone-select";
import { GetLocationList } from "../../../services/LocationService";
import OutReachOPDList from "../../Doctor/OutReachOPD/OutReachOPDList";
import { GetOutReachLocationList } from "../../../services/OutReachOPDService";
import { isDeepStrictEqual } from "util";
import { DOCTOR_TABS } from "../../../shared/Data/Doctor";
import {
  daysOfWeek,
  DoctorteamMemberTypeOptions,
  DoctorTypeOptions,
  DoctorTypeOptionsForTeamMember,
  durationOptions,
  durationTypeOptions,
  frequencyOptions,
  genderOptions,
  maritalStatusOptions,
  prefixOptions,
  roleTypeOptions,
  teamMemberTypeOptions,
} from "../../../shared/Data/TeamMember";
import { selectOption } from "../../../shared/Model/Common";

// SCHEMA FOR PARAMETER
interface Parameter {
  specialityId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  departmentId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}
// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  consumerId: "",
  orgId: "",
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

// SCHEMA FOR FORM
interface formInitialValueTypes {
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  branchId: number;
  orgId: number;
  consumerId: number;
  teamMemberId: Number;
  teamMemberTypeCode: selectOption | null;
  doctorTypeCode: selectOption | null;
  role: selectOption | null;
  locationId: selectOption | null;
  maxOutreachOpdLocation: number | null;
  departmentName: selectOption | null;
  specialtyName: selectOption | null;
  prefix: selectOption | null;
  firstName: string;
  middleName: string;
  lastName: string;
  qualification: string;
  designation: string;
  gender: selectOption | null;
  maritalStatus: selectOption | null;
  dateOfBirth: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  countryId: selectOption | null;
  countryName: string | null;
  countryRegionId: selectOption | null;
  countryRegionName: string | null;
  email: string;
  primaryMobileNumber: string;
  secondaryMobileNumber: string;
  isDeleted: boolean;
  isAnesthesia: boolean;

  folderPath: string;
  signatureOriginalName: string;
  signatureServerName: string;
  stampOriginalName: string;
  stampServerName: string;
  stampUploadMode: string;
  signatureUploadMode: string;
  availabilityData: availabilityData | null;
  outreachHospitalLocationData: [outreachHospitalLocationData];
}
const initAvailability: availabilityData = {
  availabilityId: 0,
  doctorId: 0,
  durationValue: { value: "10_MINUTES", label: "10 Minutes" },
  customValue: 10,
  customType: { value: "MINUTES", label: "Minutes" },
  durationType: null,
  timezoneValue: {
    value: "Asia/Kolkata",
    label: "(GMT+05:30) Asia/Kolkata",
  },
  availabilityDetailColl: [],
  mode: "",
  isDeleted: false,
  isSundayEnable: false,
  isMondayEnable: false,
  isTuesdayEnable: false,
  isWednesdayEnable: false,
  isThursdayEnable: false,
  isFridayEnable: false,
  isSaturdayEnable: false,
  locationId: null,
  locationName: "",
};

const initOutreachLocation: outreachHospitalLocationData = {
  availabilityOutreachLocationId: 0,
  availabilityId: 0,
  dayName: null,
  dayValue: null,
  frequencyValue: null,
  locationCode: null,
  locationId: null,
  locationName: "",
  frequency: null,
  slotDuration: null,
  slotDurationType: null,
  // outreachHospitalLocationDetailColl: [],
  outreachHospitalLocationDetailColl: [
    {
      availabilityOutreachLocationDetailId: 0,
      availabilityOutreachLocationId: 0,
      startTime: dayjs().hour(9).minute(0).toDate(),
      endTime: dayjs().hour(12).minute(0).toDate(),
      displayOrder: 0,
      parentDisplayOrder: 0,
      rowNo: 0,
      mode: "I",
      isDeleted: false,
    },
  ],
  isDeleted: false,
  mode: null,
  displayOrder: 0,
};

interface outreachHospitalLocationDetailColl {
  availabilityOutreachLocationDetailId: number;
  availabilityOutreachLocationId: number;
  startTime: any;
  endTime: any;
  displayOrder: number;
  parentDisplayOrder: number;
  rowNo: number;
  isDeleted: boolean;
  mode: string;
}
const initOutreachHospitalLocationDetail: outreachHospitalLocationDetailColl = {
  availabilityOutreachLocationDetailId: 0,
  availabilityOutreachLocationId: 0,
  startTime: dayjs().hour(9).minute(0).toDate(),
  endTime: dayjs().hour(12).minute(0).toDate(),
  displayOrder: 0,
  parentDisplayOrder: 0,
  rowNo: 0,
  mode: "",
  isDeleted: false,
};

interface outreachHospitalLocationData {
  availabilityOutreachLocationId: number;
  availabilityId: number;
  dayName: string | null;
  dayValue: string | null;
  frequencyValue: string | null;
  locationId: selectOption | null;
  locationName: string;
  locationCode: string | null;
  frequency: selectOption | null;
  slotDuration: number | null;
  slotDurationType: selectOption | null;
  outreachHospitalLocationDetailColl: Array<outreachHospitalLocationDetailColl> | null;
  isDeleted: boolean;
  mode: string | null;
  displayOrder: number;
}

interface availabilityData {
  availabilityId: number;
  doctorId: number;
  durationValue: selectOption | null;
  customType: selectOption | null;
  customValue: number;
  durationType: selectOption | null;
  timezoneValue: ITimezone | null;
  availabilityDetailColl: Array<availabilityDetailData> | null;
  mode: string;
  isDeleted: boolean;
  isSundayEnable: boolean;
  isMondayEnable: boolean;
  isTuesdayEnable: boolean;
  isWednesdayEnable: boolean;
  isThursdayEnable: boolean;
  isFridayEnable: boolean;
  isSaturdayEnable: boolean;
  locationId: selectOption | null;
  locationName: string;
}
interface availabilityDetailData {
  availabilityDetailId?: number;
  availabilityId: number;
  displayOrder: number;
  dayName: string;
  // startTime: string;
  // endTime: string;
  rowNo: number;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  mode: string;
  isDeleted: boolean;
}
//DEFAULT SCHEMA VALUE FOR FORM DETAIL
const initAvailabilityDetail: availabilityDetailData = {
  availabilityDetailId: 0,
  availabilityId: 0,
  displayOrder: 0,
  dayName: "",
  // startTime: "",
  // endTime: "",
  rowNo: 0,
  startTime: null,
  endTime: null,
  mode: "",
  isDeleted: false,
};

interface Option {
  value: string;
  label: string;
}

interface OutReachLocationData {
  hospitalColl: Array<any>;
  outReachLocationColl: Array<any>;
  totalCount: number;
}
const initOutReachLocationData: OutReachLocationData = {
  hospitalColl: [],
  outReachLocationColl: [],
  totalCount: 0,
};

// Table Data

// SCHEMA
interface Data {
  teamMemberDocumentId: number;
  name: string;
  registration: string;
  readonlegistration_BirthDate: string;
  expirationDate: string;
  documentName: string;
  size: string;
  uploadedDate: string;
  none: string;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  //onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}
// SCHEMA FOR PARAMETER
interface TableParameter {
  teamMemberDocumentId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
}
// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const tableParameter: TableParameter = {
  teamMemberDocumentId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  teamMemberDocumentColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  teamMemberDocumentColl: [],
  totalCount: 0,
};

interface TeamMemberSaveProps {
  isForPopUp?: boolean;
  handleClosePopUp?: (code: string, data?: any) => void;
  departmentObject?: selectOption | null;
  specilattyObject?: selectOption | null;
  teamMemberId?: number;
  name?: string;
}

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

export default function TeamMemberSave(Props: TeamMemberSaveProps) {
  const {
    isForPopUp = false,
    handleClosePopUp,
    departmentObject,
    specilattyObject,
    teamMemberId,
    name,
  } = Props;
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawTeamMemberId = urlParams.get("q");
  let currentTab = urlParams.get("t");
  let curTeamMemberId = 0;
  let currTabType = 0;
  // Priority: teamMemberId (from popup) > URL params
  if (teamMemberId && isForPopUp) {
    curTeamMemberId = teamMemberId;
  } else if (
    curRawTeamMemberId !== null &&
    curRawTeamMemberId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawTeamMemberId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curTeamMemberId = +decId;
    }
    currTabType = DOCTOR_TABS.find((x) => x.code === currentTab)?.value || 0;
  }

  // // DEFAULT SCHEMA VALUE FOR FORM
  // const initStaffData: formInitialValueTypes = {
  //   departmentId: null,
  //   specialityId: null,
  //   branchId: 0,
  //   orgId: 0,
  //   consumerId: 0,
  //   teamMemberId: 0,
  //   teamMemberTypeCode: null,
  //   doctorTypeCode: null,
  //   role: null,
  //   departmentName: null,
  //   specialtyName: null,
  //   prefix: null,
  //   firstName: isForPopUp ? name?.toUpperCase() : "",
  //   middleName: "",
  //   lastName: "",
  //   qualification: "",
  //   designation: "",
  //   gender: null,
  //   maritalStatus: null,
  //   dateOfBirth: "",
  //   address1: "",
  //   address2: "",
  //   city: "",
  //   postalCode: "",
  //   countryId: null,
  //   countryRegionId: null,
  //   email: "",
  //   primaryMobileNumber: "",
  //   secondaryMobileNumber: "",
  //   isDeleted: false,
  //   isAnesthesia: false,
  //   countryName: null,
  //   countryRegionName: null,
  //   folderPath: "",
  //   signatureOriginalName: "",
  //   signatureServerName: "",
  //   stampOriginalName: "",
  //   stampServerName: "",
  //   stampUploadMode: "",
  //   signatureUploadMode: "",
  //   availabilityData: initAvailability,
  //   outreachHospitalLocationData: [initOutreachLocation],
  // };

  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [param, setParam] = useState({ ...parameters, tabType: currTabType });
  const [countryRegionData, setCountryRegionData] = useState<any[]>([]); // REPLACE ANY WITH COUNTRY INTERFACE
  const [countryData, setCountryData] = useState<any[]>([]); // REPLACE ANY WITH STATE INTERFACE
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  //table
  const [tableParam, setTableParam] = useState(tableParameter);
  const [order, setOrder] = React.useState<Order>(""); // DON"T USE  DIFFERENT STATE FOR EVERY PARAMETER USE PARAMETER STATE INSTAND
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const [page_Index, setPage_Index] = useState(1);
  const [value, setValue] = useState("");
  const [counData, setCounData] = useState(0);
  const [Curr_page, setCurrPage] = React.useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [customDocPopUp, setCustomDocPopUp] = React.useState({
    teamMemberDocId: 0,
    popupTeamMemberId: curTeamMemberId,
    open: false,
  });
  const [tabProps, setTabProps] = useState("");
  const [documentsStampFile, setDocumentsStampFile] = useState<any>([]);
  const [documentsSignatureFile, setDocumentsSignatureFile] = useState<any>([]);
  const [locationData, setLocationData] = useState<any[]>([]);
  const [teamMemberLocationData, setTeamMemberLocationData] = useState<any[]>(
    [],
  );
  const [customDuration, setCustomDuration] = React.useState(false);
  const [counDataHospital, setCounDataHospital] = useState(0);
  const [totalCountHospital, setTotalCountHospital] = useState(0);
  const [pageStartIndexHospital, setPageStartIndexHospital] = useState(1);
  const [pageEndIndexHospital, setPageEndIndexHospital] = useState(1);
  const [outReachLocationData, setOutReachLocationData] =
    useState<OutReachLocationData>(initOutReachLocationData);

  const handleClickOpen = (value: boolean) => {
    setCustomDocPopUp({ ...customDocPopUp, open: value, teamMemberDocId: 0 });
  };

  const handleClose = () => {
    setCustomDocPopUp({ ...customDocPopUp, open: false });
  };

  const handleManagePopup = async (code: any) => {
    setCustomDocPopUp({ ...customDocPopUp, open: false });
    if (code === "CREATE_OR_UPDATE") {
      setIsLoading(true);
      await getTeamMemberDocumentList();
      setIsLoading(false);
    }
  };

  // UPDATE CUSTOM DOC
  const editDocument = (teamMemberDocumentId: number) => {
    setCustomDocPopUp({
      ...customDocPopUp,
      teamMemberDocId: teamMemberDocumentId,
      open: true,
    });
  };

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  // Stamp upload
  const saveStampFileSelected = (files: any) => {
    setDocumentsStampFile(files);
  };

  // DEFAULT SCHEMA VALUE FOR FORM
  const initStaffData: formInitialValueTypes = {
    departmentId: null,
    specialityId: null,
    branchId: 0,
    orgId: 0,
    consumerId: 0,
    teamMemberId: 0,
    teamMemberTypeCode: null,
    doctorTypeCode: null,
    role: null,
    locationId: null,
    maxOutreachOpdLocation: null,
    departmentName: null,
    specialtyName: null,
    prefix: null,
    firstName: isForPopUp ? name : "",
    middleName: "",
    lastName: "",
    qualification: "",
    designation: "",
    gender: null,
    maritalStatus: null,
    dateOfBirth: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    countryId: null,
    countryRegionId: null,
    email: "",
    primaryMobileNumber: "",
    secondaryMobileNumber: "",
    isDeleted: false,
    isAnesthesia: false,
    countryName: null,
    countryRegionName: null,
    folderPath: "",
    signatureOriginalName: "",
    signatureServerName: "",
    stampOriginalName: "",
    stampServerName: "",
    stampUploadMode: "",
    signatureUploadMode: "",
    availabilityData: initAvailability,
    outreachHospitalLocationData: [initOutreachLocation],
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality  is required"),
    branchId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    teamMemberId: Yup.number().nullable(),
    teamMemberTypeCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("TeamMember Type is required"),
    // doctorTypeCode: Yup.object()
    //   .shape({
    //     value: Yup.string(),
    //     label: Yup.string(),
    //   })
    //   .nullable(),
    doctorTypeCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable()
      .when("teamMemberTypeCode", {
        is: (teamMemberTypeCode: any) =>
          teamMemberTypeCode?.value?.toString() === "DOCTOR",
        then: (schema) => schema.required("Doctor Type is required"),
        otherwise: (schema) => schema.nullable(),
      }),
    role: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    locationId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable("location is required"),
    maxOutreachOpdLocation: Yup.number().nullable(),
    prefix: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    firstName: Yup.string().required("First  Name is required"),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().required("Authorized Person is required"),
    qualification: Yup.string().required("Qualification is required"),
    designation: Yup.string().required("Designation is required"),
    gender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable("Gender is required"),
    maritalStatus: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    dateOfBirth: Yup.string().nullable(),
    address1: Yup.string().required("Address is required"),
    address2: Yup.string().nullable(),
    city: Yup.string().required("City is required"),
    postalCode: Yup.string().required("PostalCode is required"),
    countryName: Yup.string().nullable(),
    countryRegionName: Yup.string().nullable(),
    countryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Country is required"),
    countryRegionId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Country Region is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    primaryMobileNumber: Yup.string().required("Mobile Number is required"),
    secondaryMobileNumber: Yup.string().nullable(),
    isDeleted: Yup.boolean(),
    isAnesthesia: Yup.boolean(),
    folderPath: Yup.string(),
    availabilityData: Yup.object()
      .shape({
        availabilityId: Yup.number().nullable(),
        doctorId: Yup.number().nullable(),
        durationValue: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .nullable(),
        customType: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .nullable(),
        customValue: Yup.number().nullable(),
        durationType: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .nullable(),
        timezoneValue: Yup.mixed().nullable(),
        mode: Yup.string(),
        isDeleted: Yup.boolean(),
        isSundayEnable: Yup.boolean().required(),
        isMondayEnable: Yup.boolean().required(),
        isTuesdayEnable: Yup.boolean().required(),
        isWednesdayEnable: Yup.boolean().required(),
        isThursdayEnable: Yup.boolean().required(),
        isFridayEnable: Yup.boolean().required(),
        isSaturdayEnable: Yup.boolean().required(),
        locationId: Yup.object()
          .shape({
            value: Yup.string(),
            label: Yup.string(),
          })
          .required("location is required")
          .nullable(),
        locationName: Yup.string().nullable(),
        availabilityDetailColl: Yup.array()
          .of(
            Yup.object()
              .shape({
                availabilityDetailId: Yup.number().nullable(),
                availabilityId: Yup.number().nullable(),
                displayOrder: Yup.number().nullable(),
                dayName: Yup.string().nullable(),
                startTime: Yup.mixed().nullable(),
                endTime: Yup.mixed().nullable(),
                mode: Yup.string().nullable(),
                isDeleted: Yup.boolean().nullable(),
              })
              .nullable(),
          )
          .nullable(),
      })
      .nullable(),
    outreachHospitalLocationData: Yup.array()
      .of(
        Yup.object()
          .shape({
            availabilityOutreachLocationId: Yup.number().nullable(),
            availabilityId: Yup.number().nullable(),
            displayOrder: Yup.number().nullable(),
            dayValue: Yup.string().nullable(),
            frequencyValue: Yup.string().nullable(),
            locationId: Yup.object()
              .shape({
                value: Yup.string(),
                label: Yup.string(),
              })
              .required("location is required")
              .nullable(),
            locationName: Yup.string().nullable(),
            locationCode: Yup.string().nullable(),
            mode: Yup.string().nullable(),
            isDeleted: Yup.boolean().nullable(),
            slotDuration: Yup.number().nullable(),
            slotDurationType: Yup.object()
              .shape({
                value: Yup.string(),
                label: Yup.string(),
              })
              .nullable(),
            outreachHospitalLocationDetailColl: Yup.array()
              .of(
                Yup.object()
                  .shape({
                    availabilityOutreachLocationDetailId:
                      Yup.number().nullable(),
                    availabilityOutreachLocationId: Yup.number().nullable(),
                    startTime: Yup.mixed().nullable(),
                    endTime: Yup.mixed().nullable(),
                    displayOrder: Yup.number().nullable(),
                    parentDisplayOrder: Yup.number().nullable(),
                    mode: Yup.string().nullable(),
                    isDeleted: Yup.boolean().nullable(),
                  })
                  .nullable(),
              )
              .nullable(),
          })
          .nullable(),
      )
      .nullable(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: {
      ...initStaffData,
      departmentId: isForPopUp ? departmentObject || null : null,
      specialityId: isForPopUp ? specilattyObject || null : null,
    }, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });
  // download stemp file
  const dowloadStampFile = (files: { name: any }) => {
    const selectedFile = documentsStampFile.find(
      (doc: { name: any }) => files?.name === doc?.name,
    );

    if (selectedFile && selectedFile.preview) {
      const downloadLink = document.createElement("a");
      downloadLink.href = selectedFile.preview; // Changed from `documentFile` to `preview` based on your data structure
      downloadLink.download = selectedFile.name; // Changed from `fileOriginalName` to `name` based on your data structure
      downloadLink.click();
    }
  };
  const dowloadSignatureFile = (files: { name: any }) => {
    const selectedFile = documentsSignatureFile.find(
      (doc: { name: any }) => files?.name === doc?.name,
    );

    if (selectedFile && selectedFile.preview) {
      const downloadLink = document.createElement("a");
      downloadLink.href = selectedFile.preview;
      downloadLink.download = selectedFile.name;
      downloadLink.click();
    }
  };
  const removeStampFile = (file: { name: any }) => {
    const updatedFiles = documentsStampFile.filter(
      (doc: { name: any }) => doc.name !== file?.name,
    );
    setDocumentsStampFile(updatedFiles);
  };

  const removeSignatureFile = (file: { name: any }) => {
    const updatedFiles = documentsSignatureFile.filter(
      (doc: { name: any }) => doc.name !== file?.name,
    );
    setDocumentsSignatureFile(updatedFiles);
  };
  // Signature upload
  const saveSignatureFileSelected = (files: any) => {
    setDocumentsSignatureFile(files);
  };
  const handleToggleDay = (
    dayName: any,
    isChecked: any,
    startTime: any,
    endTime: any,
  ) => {
    const detailColl =
      methods.getValues().availabilityData?.availabilityDetailColl || [];

    if (isChecked) {
      // Check if there are no existing rows for this day
      const isDayAlreadySet = detailColl.some(
        (row: any) => row.dayName === dayName && row.isDelete,
      );
      if (!isDayAlreadySet) {
        handleAddRow(
          dayName,
          new Date(dayjs(new Date()).format("YYYY-MM-DD") + "T09:00"),
          new Date(dayjs(new Date()).format("YYYY-MM-DD") + "T10:00"),
        ); // Pass startTime and endTime dynamically
      }
    } else {
      removeDetailField(dayName);
    }
  };

  // ADD ROW DAY
  const handleAddRow = (dayName: string, startTime: Date, endTime: Date) => {
    const availabilityData = methods.getValues().availabilityData;
    const detailColl = availabilityData?.availabilityDetailColl || [];

    const filterData =
      detailColl.filter((x: any) => x.dayName !== undefined) || [];
    const maxRowNumber = filterData.reduce(
      (acc: any, curr: any) => Math.max(acc, curr.displayOrder),
      0,
    );

    const newRow = {
      ...initAvailabilityDetail,
      availabilityId: availabilityData?.availabilityId,
      displayOrder: filterData?.length === 0 ? 0 : maxRowNumber + 1,
      dayName: dayName,
      startTime: startTime, // Use dynamic startTime
      endTime: endTime, // Use dynamic endTime
      mode: "I", // Optional: Set mode to "A" for added row if needed
    };

    if (availabilityData)
      availabilityData.availabilityDetailColl = [...filterData, newRow];

    if (availabilityData)
      availabilityData.availabilityDetailColl =
        availabilityData?.availabilityDetailColl?.filter(
          (x) => x.dayName !== undefined,
        ) || [];

    methods.setValue("availabilityData", availabilityData, {
      shouldValidate: true,
    });
  };

  const removeDetailField = (dayName: any) => {
    const data = methods.getValues();
    const detailColl = data.availabilityData?.availabilityDetailColl || [];

    for (let object of detailColl) {
      if (object.dayName === dayName) {
        object.isDeleted = true;
        object.mode = "D";
      }
    }

    methods.reset({
      ...data,
      availabilityData: {
        ...data.availabilityData,
        [`is${dayName}Enable`]: false,
        availabilityDetailColl: detailColl,
      },
    });
  };
  // HANDLE ROW FIELD CHANGE
  const handleDetailFieldChange = (
    dayName: any,
    displayOrder: number,
    field: any,
  ) => {
    const data = methods.getValues();
    const detailColl = data.availabilityData?.availabilityDetailColl || [];

    const currColl = detailColl.filter((data: any) => {
      return data.dayName !== undefined;
    });

    for (let object of currColl) {
      if (
        object.dayName === dayName &&
        object.displayOrder === displayOrder &&
        field?.toLocaleLowerCase() === "isdeleted"
      ) {
        object.isDeleted = true;
        object.mode = "D";
      }
    }

    methods.reset({
      ...methods.getValues(),
      availabilityData: {
        ...methods.getValues().availabilityData,
        availabilityDetailColl: currColl,
      },
    });
  };
  const watchedAvailabilityDetailColl = useWatch({
    name: "availabilityData.availabilityDetailColl",
    control: methods.control,
  });

  const filteredAvailabilityDetailColl =
    watchedAvailabilityDetailColl?.filter(
      (x: any) => x?.isDeleted === false && x.mode !== "D",
    ) || [];

  const watchedOutreachHospitalLocationDetailColl = useWatch({
    name: "outreachHospitalLocationData",
    control: methods.control,
  });

  // GET TeamMember DETAIL
  const getTeamMemberDetailById = async () => {
    let TeamMember: any;
    let params = new URLSearchParams();
    params.append("teamMemberId", curTeamMemberId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetTeamMemberDetail(params);
      TeamMember = res.data.data;
      //CONVERT VALUE into Label
      const staffType = teamMemberTypeOptions.find(
        (x) => x.value === TeamMember.teamMemberTypeCode,
      );
      const genderconvertlabel = genderOptions.find(
        (x) => x.value === TeamMember.gender,
      );
      const maritalStatusconvertlabel = maritalStatusOptions.find(
        (x) => x.value === TeamMember.maritalStatus,
      );
      const prefixconvertlabel = prefixOptions.find(
        (x) => x.value === TeamMember.prefix,
      );
      const doctorTypelabel = DoctorTypeOptions.find(
        (x) => x.value === TeamMember.doctorTypeCode,
      );
      const rolerTypelabel = roleTypeOptions.find(
        (x) => x.value === TeamMember.role,
      );
      let durationUnitArr =
        durationTypeOptions.filter(
          (x) => x.value === TeamMember?.durationUnit,
        ) || null;
      const outreachHospitalLocationData =
        TeamMember?.getOutreachLocationHour?.map((item: any) => {
          const matchedFreq = frequencyOptions.find(
            (opt) => opt.value === item.frequencyValue,
          );
          const matchedDura = durationTypeOptions.find(
            (opt) => opt.value === item.slotDurationType,
          );
          return {
            availabilityOutreachLocationId:
              item.availabilityOutreachLocationId || 0,
            dayName: item.dayValue
              ? {
                  value: item.dayValue,
                  label:
                    item.dayValue.charAt(0).toUpperCase() +
                    item.dayValue.slice(1).toLowerCase(),
                }
              : null,
            dayValue: item.dayValue || null,
            frequency: item.frequencyValue
              ? {
                  value: item.frequencyValue,
                  label: matchedFreq?.label || item.frequencyValue || "",
                }
              : null,
            frequencyValue: item.frequencyValue || null,
            slotDurationType: item.slotDurationType
              ? {
                  value: item.slotDurationType,
                  label: matchedDura?.label || item.slotDurationType || "",
                }
              : null,
            slotDuration: item.slotDuration || null,
            locationId: item.locationId
              ? {
                  value: item.locationId,
                  label: item.locationName,
                }
              : null,
            locationName: item.locationName || null,
            locationCode: item.locationCode || null,
            displayOrder: item.displayOrder || 0,
            isDeleted: item.isDeleted || false,
            mode: item.mode || "U",
            outreachHospitalLocationDetailColl:
              TeamMember?.getOutreachLocationHourDetail
                ?.filter(
                  (detail: any) =>
                    detail.availabilityOutreachLocationId ===
                    item.availabilityOutreachLocationId,
                )
                ?.map((detail: any) => ({
                  availabilityOutreachLocationDetailId:
                    detail.availabilityOutreachLocationDetailId || 0,
                  availabilityOutreachLocationId:
                    detail.availabilityOutreachLocationId || 0,
                  startTime: detail.startTime ? detail.startTime : null,
                  endTime: detail.endTime ? detail.endTime : null,
                  displayOrder: detail.displayOrder || 0,
                  parentDisplayOrder: item.displayOrder || 0,
                  isDeleted: detail.isDeleted || false,
                  mode: detail.mode || "U",
                })) || [],
          };
        }) || [];
      methods.reset({
        ...initStaffData,
        teamMemberId: TeamMember?.teamMemberId || 0,
        departmentId: TeamMember?.departmentId
          ? {
              value: TeamMember?.departmentId || 0,
              label: TeamMember?.departmentLabel || "",
            }
          : null,
        specialityId: TeamMember?.specialityId
          ? {
              value: TeamMember?.specialityId || 0,
              label: TeamMember?.specialtyLabel || "",
            }
          : null,
        branchId: TeamMember?.branchId || 0,
        orgId: TeamMember?.orgId || 0,
        consumerId: TeamMember?.consumerId || 0,

        teamMemberTypeCode: staffType || null,
        doctorTypeCode: doctorTypelabel || null,
        role: rolerTypelabel || null,
        departmentName: TeamMember?.departmentName
          ? {
              label: TeamMember?.departmentLabel,
              value: TeamMember?.departmentName,
            }
          : null,
        specialtyName: TeamMember?.specialtyName
          ? {
              label: TeamMember?.specialtyLabel,
              value: TeamMember?.specialtyName,
            }
          : null,
        locationId: TeamMember.locationId
          ? {
              value: TeamMember.locationId,
              label: TeamMember.locationName,
            }
          : null,
        prefix: prefixconvertlabel || null,
        firstName: TeamMember?.firstName || "",
        middleName: TeamMember?.middleName || "",
        lastName: TeamMember?.lastName || "",
        qualification: TeamMember?.qualification || "",
        designation: TeamMember?.designation || "",
        gender: genderconvertlabel || null,
        maritalStatus: maritalStatusconvertlabel || null,
        dateOfBirth: TeamMember?.dateOfBirth || "",
        email: TeamMember?.email || "",
        address1: TeamMember?.address1 || "",
        address2: TeamMember?.address2 || "",
        city: TeamMember?.city || "",
        postalCode: TeamMember?.postalCode || "",
        countryName: TeamMember?.countryName || "",
        countryRegionName: TeamMember?.countryRegionName || "",
        countryId: TeamMember?.countryId
          ? {
              label: TeamMember?.countryName || "",
              value: TeamMember?.countryId,
            }
          : null,
        countryRegionId: TeamMember?.countryRegionId
          ? {
              label: TeamMember?.countryRegionName || "",
              value: TeamMember?.countryRegionId,
            }
          : null,
        availabilityData: {
          availabilityId: TeamMember?.availabilityId || 0,
          durationValue: TeamMember?.durationValue
            ? {
                value: TeamMember?.durationValue,
                label: TeamMember?.durationLabel,
              }
            : null,
          customValue: TeamMember?.durationUnitValue || 0,
          customType:
            durationUnitArr != null
              ? {
                  value:
                    durationUnitArr != null ? durationUnitArr[0]?.value : "",
                  label:
                    durationUnitArr != null ? durationUnitArr[0]?.label : "",
                }
              : null,
          timezoneValue: TeamMember?.timezoneValue
            ? {
                value: TeamMember?.timezoneValue,
                label: TeamMember?.timezoneLabel,
              }
            : null,
          availabilityDetailColl: TeamMember?.getAvailabilityDetail || [],
          isSundayEnable: TeamMember?.isSundayEnable || false,
          isMondayEnable: TeamMember?.isMondayEnable || false,
          isTuesdayEnable: TeamMember?.isTuesdayEnable || false,
          isWednesdayEnable: TeamMember?.isWednesdayEnable || false,
          isThursdayEnable: TeamMember?.isThursdayEnable || false,
          isFridayEnable: TeamMember?.isFridayEnable || false,
          isSaturdayEnable: TeamMember?.isSaturdayEnable || false,
          locationId: TeamMember.availabilityLocationId
            ? {
                value: TeamMember.availabilityLocationId,
                label: TeamMember.availabilityLocationName,
              }
            : null,
          locationName: TeamMember.availabilityLocationName || null,
        },
        primaryMobileNumber: TeamMember?.primaryMobileNumber || "",
        secondaryMobileNumber: TeamMember?.secondaryMobileNumber || "",
        isAnesthesia: TeamMember?.isAnesthesia || false,
        folderPath: TeamMember?.folderPath || "",
        signatureOriginalName: TeamMember?.signatureOriginalName || "",
        signatureServerName: TeamMember?.signatureServerName || "",
        stampOriginalName: TeamMember?.stampOriginalName || "",
        stampServerName: TeamMember?.stampServerName || "",
        outreachHospitalLocationData: outreachHospitalLocationData || [],
        maxOutreachOpdLocation: TeamMember?.maxOutreachOpdLocation || 0,
      });
      if (TeamMember?.stampServerName) {
        const fileName = TeamMember.stampOriginalName;
        const stampfile = dataURLtoFile(TeamMember.stampServerImage, fileName);
        setDocumentsStampFile([stampfile]);
      }
      if (TeamMember?.signatureServerName) {
        const fileName = TeamMember.signatureOriginalName;
        const signaturefile = dataURLtoFile(
          TeamMember.signatureServerImage,
          fileName,
        );
        setDocumentsSignatureFile([signaturefile]);
      }
      if (TeamMember?.durationValue === "CUSTOM") {
        setCustomDuration(true);
      }

      setIsLoading(false);
      return TeamMember;
    } catch (e: any) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE STAFF
  const saveTeamMember = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = new FormData();

      let availabilityDetailFilteredColl =
        value.availabilityData?.availabilityDetailColl?.filter(
          (x) => x.dayName !== undefined,
        ) || null;

      let detailColl: availabilityDetailData[] = (
        availabilityDetailFilteredColl ?? []
      ).map((item: availabilityDetailData) => {
        return {
          ...item,
          // startTime: item.startTime ? dayjs(item.startTime) : null,
          // endTime: item.endTime ? dayjs(item.endTime) : null,
          startTime: item.startTime
            ? new Date(item?.startTime).toLocaleTimeString()
            : null,
          endTime: item.endTime
            ? new Date(item?.endTime).toLocaleTimeString()
            : null,
        };
      });

      // FOR OUTREACH
      const outreachLocationDetailData =
        value.outreachHospitalLocationData?.reduce((acc: any, curr: any) => {
          if (curr.outreachHospitalLocationDetailColl?.length) {
            const details = curr.outreachHospitalLocationDetailColl.map(
              (slot: any) => ({
                availabilityOutreachLocationDetailId:
                  slot.availabilityOutreachLocationDetailId ?? null,
                availabilityOutreachLocationId:
                  slot.availabilityOutreachLocationId ?? 0,
                startTime: slot.startTime
                  ? new Date(slot?.startTime).toLocaleTimeString()
                  : null,
                endTime: slot.endTime
                  ? new Date(slot?.endTime).toLocaleTimeString()
                  : null,
                isDeleted: slot.isDeleted ?? false,
                displayOrder: slot.displayOrder,
                parentDisplayOrder: slot.parentDisplayOrder,
                mode: slot.mode ?? "I",
              }),
            );

            acc = [...acc, ...details];
          }
          return acc;
        }, []) || [];
      const outreachLocationData =
        value.outreachHospitalLocationData?.map((x) => ({
          availabilityOutreachLocationId: x.availabilityOutreachLocationId ?? 0,
          locationId: x.locationId?.value ?? null,
          locationCode: x.locationCode ?? null,
          dayValue: x.dayName?.value ?? null,
          frequencyValue: x.frequency?.value ?? null,
          displayOrder: x.displayOrder ?? null,
          slotDuration: x.slotDuration ?? null,
          slotDurationType: x.slotDurationType?.value ?? null,
          location: null,
          frequency: null,
          dayName: null,
          outreachHospitalLocationDetailColl: null,
          mode: x.mode ?? "I",
          isDeleted: x.isDeleted ?? false,
        })) || [];

      formData.append("teamMemberId", value?.teamMemberId.toString());
      formData.append("departmentId", value.departmentId?.value || "");
      formData.append("specialityId", value.specialityId?.value || "");
      formData.append("branchId", userContext?.branchId.toString());
      formData.append("orgId", userContext?.organizationId.toString());
      formData.append("consumerId", userContext?.consumerId.toString());
      // formData.append("locationId", value.locationId?.value || "" );
      formData.append(
        "teamMemberTypeCode",
        value.teamMemberTypeCode?.value || "",
      );
      formData.append(
        "maxOutreachOpdLocation",
        value?.maxOutreachOpdLocation || "0",
      );
      formData.append("doctorTypeCode", value.doctorTypeCode?.value || "");
      formData.append("role", value.role?.value || "");
      formData.append("defaultLocationId", value.locationId?.value || "");
      formData.append("isAnesthesia", value.isAnesthesia ? "true" : "false");
      formData.append("prefix", value.prefix?.value || "");
      formData.append("firstName", value.firstName?.trim() || "");
      formData.append("middleName", value.middleName?.trim() || "");
      formData.append("lastName", value.lastName?.trim() || "");
      formData.append("qualification", value.qualification?.trim() || "");
      formData.append("designation", value.designation?.trim() || "");
      formData.append("gender", value.gender?.value || "");
      formData.append("maritalStatus", value.maritalStatus?.value || "");
      formData.append("dateOfBirth", value.dateOfBirth?.trim() || "");
      formData.append("address1", value.address1?.trim() || "");
      formData.append("address2", value.address2?.trim() || "");
      formData.append("city", value.city?.trim() || "");
      formData.append("postalCode", value.postalCode?.trim() || "");
      formData.append("countryRegionId", value.countryRegionId?.value || "0");
      formData.append("countryId", value.countryId?.value || "0");
      formData.append("countryName", value.countryName || "");
      formData.append("countryRegionName", value.countryRegionName || "");
      formData.append("email", value.email?.trim() || "");

      formData.append(
        "primaryMobileNumber",
        value.primaryMobileNumber?.trim() || "",
      );
      formData.append(
        "secondaryMobileNumber",
        value.secondaryMobileNumber?.trim() || "",
      );
      formData.append(
        "DataManagedBy",
        userContext.loginDetail.userId?.toString() || "",
      );

      formData.append("folderPath", value.folderPath);
      formData.append("StampFile", documentsStampFile[0]);
      formData.append("StampUploadMode", "I");
      formData.append("StampOriginalName", value.stampOriginalName);
      formData.append("StampServerName", value.stampServerName);
      formData.append("StampUpdateName", value.stampServerName);
      formData.append("SignatureFile", documentsSignatureFile[0]);
      formData.append("SignatureUploadMode", "I");
      formData.append("SignatureOriginalName", value.signatureOriginalName);
      formData.append("SignatureServerName", value.signatureServerName);
      formData.append("SignatureUpdateName", value.signatureServerName);

      // FOR AVAILABILITY
      formData.append(
        "availabilityId",
        value.availabilityData?.availabilityId || 0,
      );
      formData.append(
        "durationValue",
        value.availabilityData?.durationValue?.value || 0,
      );
      formData.append(
        "durationLabel",
        value.availabilityData?.durationValue?.label.toString() || "",
      );
      formData.append(
        "durationUnitValue",
        value.availabilityData?.customValue || 0,
      );
      formData.append(
        "durationUnit",
        value.availabilityData?.customType?.value || 0,
      );
      formData.append(
        "timezoneValue",
        value.availabilityData?.timezoneValue?.value || 0,
      );
      formData.append(
        "timezoneLabel",
        value.availabilityData?.timezoneValue?.label || "0",
      );
      formData.append(
        "availabilityDetail",
        detailColl && detailColl.length > 0 ? JSON.stringify(detailColl) : "",
      );
      formData.append(
        "isSundayEnable",
        value.availabilityData?.isSundayEnable.toString() || "0",
      );
      formData.append(
        "isMondayEnable",
        value.availabilityData?.isMondayEnable.toString() || "0",
      );
      formData.append(
        "isTuesdayEnable",
        value.availabilityData?.isTuesdayEnable.toString() || "0",
      );
      formData.append(
        "isWednesdayEnable",
        value.availabilityData?.isWednesdayEnable.toString() || "0",
      );
      formData.append(
        "isThursdayEnable",
        value.availabilityData?.isThursdayEnable.toString() || "0",
      );
      formData.append(
        "isFridayEnable",
        value.availabilityData?.isFridayEnable.toString() || "0",
      );
      formData.append(
        "isSaturdayEnable",
        value.availabilityData?.isSaturdayEnable.toString() || "0",
      );
      formData.append(
        "availabilityData",
        JSON.stringify(value.availabilityData),
      );
      formData.append(
        "availabilityLocationId",
        value.availabilityData?.locationId?.value || "0",
      );
      formData.append(
        "outreachLocationDetail",
        outreachLocationData ? JSON.stringify(outreachLocationData) : "",
      );
      formData.append(
        "outreachLocationDetailData",
        outreachLocationDetailData
          ? JSON.stringify(outreachLocationDetailData)
          : "",
      );
      if (curTeamMemberId > 0) {
        result = await UpdateTeamMember(formData);
      } else {
        result = await CreateTeamMember(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          if (curTeamMemberId <= 0) {
            navigate(-1);
          }
        } else {
          handleClosePopUp?.(
            curTeamMemberId > 0 ? "TEAM_MEMBER_UPDATE" : "TEAM_MEMBER_CREATE",
            {
              teamMemberId: result?.data?.teamMemberId,
              teamMemberLabel: `${value.firstName} ${value.lastName}`,
            },
          );
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET COUNTRIES
  async function fetchCountry() {
    try {
      let params = new URLSearchParams();
      const countryData: any = await GetCountryListForSelect(params);
      if (countryData?.data?.data) {
        let countyCollection = countryData.data.data;
        setCountryData(countyCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH STATE FOR COUNTRY
  async function fetchCountryRegionData(countryId) {
    try {
      let params = new URLSearchParams();
      params.append("countryId", countryId);
      const localCountryRegionData: any =
        await GetCountryRegionListForSelect(params);
      if (localCountryRegionData?.data?.data) {
        let stateCollection = localCountryRegionData.data.data;
        setCountryRegionData(stateCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
      }
      // setIsLoading(false);
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      // setIsLoading(false);
    }
  }

  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
      }
      //setIsLoading(false);
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      // setIsLoading(false);
    }
  }

  //Table Data

  // STATE FOR STORAGE
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null,
  );

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    teamMemberDocumentId: any,
  ) => {
    setAnchorEl3(event.currentTarget);
    setCustomDocPopUp({
      ...customDocPopUp,
      teamMemberDocId: teamMemberDocumentId,
    });
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Registration #
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Registration / Birth Date
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="left"
          >
            Expiration Date
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "15%",
            }}
            align="left"
          >
            Document Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="right"
          >
            Size
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "20%",
            }}
            align="left"
          >
            Uploaded Date
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="left"
          >
            {""}
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // GET LIST OF STAFF DOCUMENT
  const getTeamMemberDocumentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("teamMemberId", curTeamMemberId?.toString());
      params.append("branchId", userContext?.branchId.toString());
      params.append("consumerId", userContext?.consumerId.toString());
      params.append("orgId", userContext?.organizationId.toString());
      params.append("pageIndex", tableParam.pageIndex?.toString());
      params.append("pageSize", tableParam.pageSize?.toString());
      params.append("sortColumn", tableParam.sortColumn);
      params.append("sortDirection", tableParam.sortDirection);
      params.append("searchValue", tableParam.searchValue);
      const result: any = await GetTeamMemberDocumentList(params);
      if (result?.data?.data?.teamMemberDocumentColl) {
        const resultData: ResultData = result?.data?.data;
        let teamMemberDocumentColl = resultData?.teamMemberDocumentColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / tableParam.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (page_Index - 1) * tableParam.pageSize + 1;
        let endIndex =
          (page_Index - 1) * tableParam.pageSize +
          (teamMemberDocumentColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (Curr_page !== value) {
      setCurrPage(value);
      setPage_Index(value);
      setTableParam({ ...tableParam, pageIndex: value });
    }
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value: any) => {
    setOpenDeleteDilog(value);
  };

  // HANDLE DELETE
  async function deleteRow(teamMemberDocumentIds: any) {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        teamMemberDocumentIds: teamMemberDocumentIds?.toString(),
        teamMemberId: curTeamMemberId?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        orgId: userContext?.organizationId?.toString(),
        branchId: userContext?.branchId?.toString(),
        isDeleted: true,
        dataManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteTeamMemberDocument(DeleteData);
      if (result) {
        await getTeamMemberDocumentList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.teamMemberDocumentColl?.map(
        (n: any) => n?.teamMemberDocumentId || 0,
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const getFormattedFileSize = (bytes, decimals = 0) => {
    if (bytes === 0) return "0 Bytes";
    // JV
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // TAB CAHNGE -- PLEASE USE PARAMETER STATE FOR ANY SEARCH OR ANYTHING THAT AFFECT LIST RESULT
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setParam({ ...param, tabType: newValue });
    const tabCode =
      DOCTOR_TABS.find((x) => x.value === newValue)?.code || "general-info";
    navigate(
      {
        // pathname: teamMember.pathname,
        search: `?q=${encodeURIComponent(
          edService.encrypt(curTeamMemberId) || "",
        )}&t=${tabCode}`,
      },
      { replace: true },
    );
  };
  // ON SUBMIT OF FORM
  const onSubmit = (data: any) => {
    saveTeamMember(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp?.("CLOSE");
    } else {
      navigate(-1);
    }
  };
  const tabTypeDocument =
    methods.getValues().doctorTypeCode?.value === "CONSULTING_DOCTOR" ? 2 : 2;
  const tabTypeAvailability =
    methods.getValues().doctorTypeCode?.value === "CONSULTING_DOCTOR" ? 3 : 3;
  const tabTypeOutReachOPD =
    methods.getValues().doctorTypeCode?.value === "CONSULTING_DOCTOR" ? 4 : 4;

  async function fetchLocation(searchValue: string, oninit: boolean) {
    try {
      let params = new URLSearchParams();
      let locationCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("isContainBranch", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      const response: any = await GetLocationList(params);
      if (response?.data?.data?.locationColl) {
        locationCollection = response.data.data.locationColl;
        if (oninit) setLocationData(locationCollection);
        const routineLocation =
          locationCollection.find((x: any) => x.code === "BRANCH") || null;

        methods.reset({
          ...methods.getValues(),
          availabilityData: {
            ...methods.getValues()?.availabilityData,
            locationId: routineLocation
              ? {
                  value: routineLocation.locationId,
                  label: routineLocation.locationName,
                }
              : null,
            locationName: routineLocation?.locationName || "",
          },
        });
      }
      return locationCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }
  async function fetchTeamMemberLocationList(
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let params = new URLSearchParams();
      let locationCollection = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("teamMemberId", curTeamMemberId?.toString());
      params.append("isContainBranch", "true");
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      const response: any = await GetTeamMemberLocationList(params);
      if (response?.data?.data?.locationColl) {
        locationCollection = response.data.data.locationColl;
        if (oninit) setTeamMemberLocationData(locationCollection);
        const routineLocation =
          locationCollection.find(
            (x: any) => x.locationTypeCode === "BRANCH",
          ) || null;

        methods.reset({
          ...methods.getValues(),
          availabilityData: {
            ...methods.getValues()?.availabilityData,
            locationId: routineLocation
              ? {
                  value: routineLocation.locationId,
                  label: routineLocation.locationName,
                }
              : null,
            locationName: routineLocation?.locationName || "",
          },
        });
      }
      return locationCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }
  const getOutReachLocationList = async () => {
    setIsLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("doctorId", curTeamMemberId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetOutReachLocationList(params);
      if (result?.data?.data?.outReachLocationColl) {
        const resultData: ResultData = result?.data?.data;
        let outReachLocationColl = resultData?.outReachLocationColl || [];
        let dataCount = result?.data?.data?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounDataHospital(showDataCount);
        setOutReachLocationData(resultData);
        let startIndex = (page_Index - 1) * tableParam.pageSize + 1;
        let endIndex =
          (page_Index - 1) * tableParam.pageSize +
          (outReachLocationColl?.length || 0);
        setPageStartIndexHospital(startIndex);
        setPageEndIndexHospital(endIndex);
        setTotalCountHospital(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchCountry();
      await fetchSpeciality("");
      await fetchLocation("", true);
      await fetchDepartment();
      await getOutReachLocationList();
      if (curTeamMemberId > 0) {
        const TeamMember = await getTeamMemberDetailById();
        if (TeamMember?.countryId) {
          await fetchCountryRegionData(TeamMember?.countryId);
        }
      } else {
        // await fetchCountryRegionData(methods.getValues()?.countryId?.value);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };
  function searchValue(val: any) {
    if (val === "") {
      setValue("");
    }
    setPage_Index(1);
    setParam({ ...param, searchValue: val.toString(), pageIndex: 1 });
  }

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curTeamMemberId]);

  useEffect(() => {
    setIsLoading(true);
    getTeamMemberDocumentList();
  }, [tableParam]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid
                item
                xs={12}
                sx={{
                  paddingTop: 2,
                }}
              >
                <Tabs
                  value={param.tabType}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  indicatorColor="primary"
                  aria-label="staff"
                  TabIndicatorProps={{ sx: { backgroundColor: " #2e67a3" } }}
                  sx={{
                    maxWidth: "100%",
                    "& button:hover": {
                      backgroundColor: "#f1f6fe",
                      borderTopLeftRadius: "10px",
                      borderTopRightRadius: "10px",
                    },
                  }}
                >
                  <Tab
                    sx={{ textTransform: "capitalize !important" }}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span>General Information</span>
                      </Box>
                    }
                  />
                  <Tab
                    sx={{ textTransform: "capitalize !important" }}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span>Documents</span>
                      </Box>
                    }
                  />
                  {methods.getValues().doctorTypeCode?.value ===
                  "CONSULTING_DOCTOR" ? (
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Stamp & Signature</span>
                        </Box>
                      }
                    />
                  ) : null}

                  {methods.getValues().doctorTypeCode?.value ===
                  "CONSULTING_DOCTOR" ? (
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Availability</span>
                        </Box>
                      }
                    />
                  ) : null}

                  {methods.getValues().doctorTypeCode?.value ===
                    "CONSULTING_DOCTOR" && curTeamMemberId > 0 ? (
                    <Tab
                      sx={{ textTransform: "capitalize !important" }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>Outreach OPD / Location</span>
                        </Box>
                      }
                    />
                  ) : null}
                </Tabs>
              </Grid>
              {param.tabType === 0 && (
                <>
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">Basic Information</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.teamMemberTypeCode
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Team Member Type *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="teamMemberTypeCode"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isDisabled={curTeamMemberId > 0}
                                  isNotCreatable={true}
                                  id={"teamMemberTypeCode"}
                                  FieldName={"teamMemberTypeCode"}
                                  options={teamMemberTypeOptions}
                                  // options={
                                  //   userContext.loginDetail.userRole ===
                                  //     "superadmin" || curTeamMemberId > 0
                                  //     ? teamMemberTypeOptions
                                  //     : DoctorteamMemberTypeOptions
                                  // }
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  getOptionValue={(option: any) => option.value}
                                  getOptionLabel={(option: any) => option.label}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.teamMemberTypeCode?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "teamMemberTypeCode",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                    if (e?.value?.toString() === "DOCTOR") {
                                      if (DoctorTypeOptions.length === 1) {
                                        methods.setValue(
                                          "doctorTypeCode",
                                          DoctorTypeOptions[0],
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.teamMemberTypeCode
                                ?.message
                            }
                          </Typography>
                        </FormControl>
                      </Grid>
                      {methods
                        ?.getValues()
                        ?.teamMemberTypeCode?.value?.toString() ===
                        "DOCTOR" && (
                        <>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text  ${
                                  methods.formState.errors.doctorTypeCode
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                Doctor Type *
                              </FormLabel>
                              <SkeletonWrapper
                                isLoading={showSkeleton}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="doctorTypeCode"
                                  render={({ field }) => (
                                    <CustomSelect
                                      {...field}
                                      isNotCreatable={true}
                                      id={"doctorTypeCode"}
                                      FieldName={"doctorTypeCode"}
                                      isDisabled={curTeamMemberId > 0}
                                      // isDisabled={false}
                                      // options={
                                      //   userContext.loginDetail.userRole ===
                                      //     "superadmin" || curTeamMemberId > 0
                                      //     ? DoctorTypeOptions
                                      //     : DoctorTypeOptionsReferral
                                      // }
                                      // options={DoctorTypeOptions}
                                      options={
                                        userContext.loginDetail.userRole ===
                                        "superadmin"
                                          ? DoctorTypeOptions
                                          : DoctorTypeOptionsForTeamMember
                                      }
                                      openMenuOnFocus
                                      isClearable
                                      isSearchable
                                      getOptionValue={(option: any) =>
                                        option.value
                                      }
                                      getOptionLabel={(option: any) =>
                                        option.label
                                      }
                                      menuPortalTarget={document.querySelector(
                                        "body",
                                      )}
                                      handleChange={async (e: any) => {
                                        if (
                                          methods
                                            ?.getValues()
                                            ?.doctorTypeCode?.value?.toString() !==
                                          e?.value?.toString()
                                        ) {
                                          methods.setValue(
                                            "doctorTypeCode",
                                            e?.value ? e : null,
                                            {
                                              shouldValidate: true,
                                            },
                                          );
                                        }
                                      }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>
                              <Typography className="validation-message">
                                {
                                  methods.formState.errors.doctorTypeCode
                                    ?.message
                                }
                              </Typography>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                              <FormLabel
                                className={`fileds-label-text ${
                                  methods.formState.errors.isAnesthesia
                                    ? "validation-message"
                                    : ""
                                }`}
                              >
                                {}
                              </FormLabel>

                              <SkeletonWrapper
                                isLoading={showSkeleton}
                                className="skeleton-input"
                              >
                                <Controller
                                  control={methods.control}
                                  name="isAnesthesia"
                                  render={({ field }) => (
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          {...field}
                                          checked={field.value || false}
                                          onChange={(e) =>
                                            field.onChange(e.target.checked)
                                          }
                                        />
                                      }
                                      label="IS ANAESTHESIOLOGIST"
                                      labelPlacement="end"
                                      sx={{ marginTop: "18px" }}
                                    />
                                  )}
                                />
                              </SkeletonWrapper>

                              <Typography className="validation-message">
                                {methods.formState.errors.isAnesthesia?.message}
                              </Typography>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.role
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Role *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="role"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"role"}
                                  FieldName={"role"}
                                  isDisabled={curTeamMemberId > 0}
                                  options={roleTypeOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  getOptionValue={(option: any) => option.value}
                                  getOptionLabel={(option: any) => option.label}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.role?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "role",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.role?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.locationId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Location *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="locationId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"locationId"}
                                  FieldName={"locationId"}
                                  options={locationData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.locationId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "locationId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.locationId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      {userContext.loginDetail.userRole === "superadmin" && (
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <FormLabel
                              className={`fileds-label-text  ${
                                methods.formState.errors.maxOutreachOpdLocation
                                  ? "validation-message"
                                  : ""
                              }`}
                            >
                              Max Outreach OPD / Location *
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder=""
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                inputProps={{
                                  className: "no-spinner",
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                  step: "1",
                                  onKeyPress: (event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  },
                                }}
                                {...methods.register("maxOutreachOpdLocation", {
                                  required: true,
                                })}
                                error={
                                  !!methods.formState.errors
                                    .maxOutreachOpdLocation
                                }
                                className={`text-input ${
                                  methods.formState.errors
                                    .maxOutreachOpdLocation
                                    ? "error"
                                    : ""
                                }`}
                              />
                            </SkeletonWrapper>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={9}></Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.departmentId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Department *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="departmentId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"departmentId"}
                                  FieldName={"departmentId"}
                                  options={departmentData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e: any) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.departmentId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "departmentId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue("specialityId", null, {
                                        shouldValidate: true,
                                      });
                                      methods.setValue("specialityName", null, {
                                        shouldValidate: true,
                                      });
                                    }
                                    if (e?.value) {
                                      setIsLoading(true);
                                      await fetchSpeciality(e.value);
                                      setIsLoading(false);
                                    } else {
                                      setspecialityData([]);
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.departmentId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.specialityId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Speciality *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="specialityId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"specialityId"}
                                  FieldName={"specialityId"}
                                  options={specialityData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  getOptionValue={(option: any) => option.value}
                                  getOptionLabel={(option: any) => option.label}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.specialityId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "specialityId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.specialityId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.prefix
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Prefix
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="prefix"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"prefix"}
                                  FieldName={"prefix"}
                                  options={prefixOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  getOptionValue={(option: any) => option.value}
                                  getOptionLabel={(option: any) => option.label}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.prefix?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "prefix",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.prefix?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.firstName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            First Name *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("firstName")}
                              error={!!methods.formState.errors.firstName}
                              className={`text-input ${
                                methods.formState.errors.firstName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.firstName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.middleName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Middle Name
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("middleName")}
                              error={!!methods.formState.errors.middleName}
                              className={`text-input ${
                                methods.formState.errors.middleName
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.middleName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.lastName
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Last Name *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("lastName")}
                              error={!!methods.formState.errors.lastName}
                              className={`text-input ${
                                methods.formState.errors.lastName ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.lastName?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.qualification
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Qualification *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("qualification")}
                              error={!!methods.formState.errors.qualification}
                              className={`text-input ${
                                methods.formState.errors.qualification
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.qualification?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.designation
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Designation *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("designation")}
                              error={!!methods.formState.errors.designation}
                              className={`text-input ${
                                methods.formState.errors.designation
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.designation?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.gender
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Gender *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="gender"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"gender"}
                                  FieldName={"gender"}
                                  options={genderOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  getOptionValue={(option: any) => option.value}
                                  getOptionLabel={(option: any) => option.label}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.gender?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "gender",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.gender?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.maritalStatus
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Marital Status
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="maritalStatus"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"maritalStatus"}
                                  FieldName={"maritalStatus"}
                                  options={maritalStatusOptions}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  getOptionValue={(option: any) => option.value}
                                  getOptionLabel={(option: any) => option.label}
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.maritalStatus?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "maritalStatus",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                    }
                                  }}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.maritalStatus?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.dateOfBirth
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Date of Birth
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <DatePickerComponent
                              format="DD-MM-YYYY"
                              disabled={false}
                              mode="date"
                              slotProps={{ textField: { size: "small" } }}
                              value={
                                methods?.getValues()?.dateOfBirth
                                  ? dayjs(methods?.getValues()?.dateOfBirth)
                                  : null
                              }
                              onChange={(e: any) => {
                                if (e !== null && isValid(e.$d)) {
                                  let savedDateFormate: any = format(
                                    e.$d,
                                    "yyyy-MM-dd",
                                  );
                                  methods.setValue(
                                    "dateOfBirth",
                                    savedDateFormate,
                                  );
                                } else {
                                  methods.setValue("dateOfBirth", "");
                                }
                              }}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className={"group-container"}>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6">
                          Contact Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.address1
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Address 1{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("address1")}
                              error={!!methods.formState.errors.address1}
                              className={`text-input ${
                                methods.formState.errors.address1 ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.address1?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.address2
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Address 2{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("address2")}
                              error={!!methods.formState.errors.address2}
                              className={`text-input ${
                                methods.formState.errors.address2 ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.address2?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.city
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            City{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("city")}
                              error={!!methods.formState.errors.city}
                              className={`text-input ${
                                methods.formState.errors.city ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.city?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.postalCode
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Postal Code *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              id="postalCode"
                              size="small"
                              {...methods.register("postalCode")}
                              error={!!methods.formState.errors.postalCode}
                              className={`text-input ${
                                methods.formState.errors.postalCode
                                  ? "error"
                                  : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.postalCode?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Country *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryId"}
                                  FieldName={"countryId"}
                                  options={countryData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={async (e) => {
                                    if (
                                      methods
                                        ?.getValues()
                                        ?.countryId?.value?.toString() !==
                                      e?.value?.toString()
                                    ) {
                                      methods.setValue(
                                        "countryId",
                                        e?.value ? e : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryName",
                                        e?.value ? e?.label : null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryRegionId",
                                        null,
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      methods.setValue(
                                        "countryRegionName",
                                        "",
                                        {
                                          shouldValidate: true,
                                        },
                                      );
                                      if (e?.value !== null) {
                                        setIsLoading(true);
                                        await fetchCountryRegionData(e?.value);
                                        setIsLoading(false);
                                      } else {
                                        setCountryRegionData([]);
                                      }
                                    }
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.postalCode
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.countryRegionId
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            State *
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <Controller
                              control={methods.control}
                              name="countryRegionId"
                              render={({ field }) => (
                                <CustomSelect
                                  {...field}
                                  isNotCreatable={true}
                                  id={"countryRegionId"}
                                  FieldName={"countryRegionId"}
                                  options={countryRegionData}
                                  openMenuOnFocus
                                  isClearable
                                  isSearchable
                                  menuPortalTarget={document.querySelector(
                                    "body",
                                  )}
                                  handleChange={(e) => {
                                    methods.setValue(
                                      "countryRegionId",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                    methods.setValue(
                                      "countryRegionName",
                                      e?.value ? e?.label : null,
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  className={`text-input ${
                                    methods.formState.errors.countryId
                                      ? "error"
                                      : ""
                                  }`}
                                />
                              )}
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {methods.formState.errors.countryRegionId?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.email
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Email{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <CustomTextField
                              placeholder=""
                              autoCapitalize="none"
                              type="text"
                              fullWidth
                              size="small"
                              {...methods.register("email")}
                              error={!!methods.formState.errors.email}
                              className={`text-input ${
                                methods.formState.errors.email ? "error" : ""
                              }`}
                            />
                          </SkeletonWrapper>
                        </FormControl>
                        <Typography className="validation-message">
                          {methods.formState.errors.email?.message}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.primaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Primary Mobile Number{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="primaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              {...methods.register("primaryMobileNumber")}
                              error={
                                methods.formState.errors.primaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.primaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              onChange={(e) => {
                                methods.setValue(
                                  "primaryMobileNumber",
                                  e || "",
                                  { shouldValidate: true },
                                );
                              }}
                              value={methods.getValues()?.primaryMobileNumber}
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.primaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <div className="input-group">
                          <FormLabel
                            className={`fileds-label-text  ${
                              methods.formState.errors.secondaryMobileNumber
                                ? "validation-message"
                                : ""
                            }`}
                          >
                            Secondary Mobile Number{" "}
                            <CustomTooltip
                              title="This field is important to be filled for better communication"
                              hideHeader={true}
                            ></CustomTooltip>
                          </FormLabel>
                          <SkeletonWrapper
                            isLoading={showSkeleton}
                            className="skeleton-input"
                          >
                            <PhoneInput
                              id="secondaryMobileNumber"
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="IN"
                              {...methods.register("secondaryMobileNumber")}
                              error={
                                methods.formState.errors.secondaryMobileNumber
                                  ?.message
                              }
                              className={`phone-input ${
                                methods.formState.errors.secondaryMobileNumber
                                  ? "error"
                                  : ""
                              }`}
                              value={methods.getValues()?.secondaryMobileNumber}
                              onChange={(e) => {
                                methods.setValue(
                                  "secondaryMobileNumber",
                                  e || "",
                                );
                              }}
                              placeholder="Enter phone number"
                            />
                          </SkeletonWrapper>
                          <Typography className="validation-message">
                            {
                              methods.formState.errors.secondaryMobileNumber
                                ?.message
                            }
                          </Typography>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
              {param.tabType === 1 && (
                <>
                  <Grid container className="list-container">
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item>
                        <Typography variant="h6">Documents</Typography>
                      </Grid>
                      <Grid item>
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => handleClickOpen(true)}
                          size="medium"
                          startIcon={<AddIcon />}
                          disabled={curTeamMemberId <= 0}
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12} mt={2}>
                      <TableContainer>
                        <Table>
                          <EnhancedTableHead
                            numSelected={
                              resultData?.teamMemberDocumentColl?.length || 0
                            }
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleCheckSelectAll}
                            //onRequestSort={handleRequestSort}
                            rowCount={
                              resultData?.teamMemberDocumentColl?.length
                            }
                          />
                          <TableBody>
                            {showSkeleton ? (
                              Array(skeletonRowCount)
                                ?.fill(0)
                                ?.map((skeletonRow, index) => {
                                  return (
                                    <TableRow
                                      key={"skeleton-row" + index?.toString()}
                                    >
                                      <TableCell colSpan={8}>
                                        <SkeletonWrapper
                                          isLoading={showSkeleton}
                                          className="skeleton-input"
                                        >
                                          <span></span>
                                        </SkeletonWrapper>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                            ) : (
                              <>
                                {stableSort(
                                  resultData?.teamMemberDocumentColl,
                                  getComparator(order, orderBy),
                                )
                                  .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage,
                                  )
                                  .map((item: any, index: any) => {
                                    return (
                                      <TableRow
                                        key={item?.teamMemberDocumentId?.toString()}
                                      >
                                        <TableCell
                                          onClick={() =>
                                            editDocument(
                                              item?.teamMemberDocumentId,
                                            )
                                          }
                                        >
                                          <Typography
                                            className="text-link"
                                            sx={{ cursor: "pointer" }}
                                          >
                                            {item.documentName}
                                          </Typography>
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.registrationNumber}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {format(
                                            new Date(item.registrationDate),
                                            "dd-MM-yyyy",
                                          )}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {format(
                                            new Date(item.expirationDate),
                                            "dd-MM-yyyy",
                                          )}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.originalFileName}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                          align="right"
                                        >
                                          {item.fileSize === null
                                            ? "-"
                                            : getFormattedFileSize(
                                                item.fileSize,
                                              )}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            display: {
                                              xs: "none",
                                              sm: "table-cell",
                                              md: "table-cell",
                                              lg: "table-cell",
                                            },
                                          }}
                                        >
                                          {item.uploadedDate}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Tooltip title="More" placement="top">
                                            <IconButton
                                              onClick={(e: any) => {
                                                handleOpenMore(
                                                  e,
                                                  item?.teamMemberDocumentId ||
                                                    0,
                                                );
                                              }}
                                            >
                                              <MoreVertRoundedIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                {resultData?.teamMemberDocumentColl?.length <=
                                  0 && (
                                  <TableRow>
                                    <TableCell align="center" colSpan={8}>
                                      No record(s) found
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12} paddingTop={1}>
                      {resultData?.teamMemberDocumentColl?.length > 0 && (
                        <Box className="pagination">
                          <Box className="pagination-label">
                            <SkeletonWrapper
                              isLoading={showSkeleton}
                              className="skeleton-pagination-label"
                            >
                              {pageStartIndex} - {pageEndIndex} of {totalCount}
                            </SkeletonWrapper>
                          </Box>
                          <Box className="pagination-stack">
                            <SkeletonWrapper
                              isLoading={showSkeleton}
                              className="skeleton-pagination-stack"
                            >
                              <Stack component="div">
                                <Pagination
                                  color="primary"
                                  count={counData} // NUMBER OF PAGES
                                  page={Curr_page} // CURRENT PAGE
                                  onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                                />
                              </Stack>
                            </SkeletonWrapper>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </>
              )}
              {param.tabType === tabTypeDocument && (
                <Grid item xs={12} className={"group-container"}>
                  <Grid item container xs={12} spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel className="fileds-label-text">
                          Upload Stamp
                        </FormLabel>
                        <MultiUpload
                          onDropandSelect={saveStampFileSelected}
                          editFile={documentsStampFile}
                          MAX_COUNT={1}
                          onFileDownload={dowloadStampFile}
                          onFileRemove={(file: any) => {
                            removeStampFile(file);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel className="fileds-label-text">
                          Upload Signaure
                        </FormLabel>
                        <MultiUpload
                          onDropandSelect={saveSignatureFileSelected}
                          editFile={documentsSignatureFile}
                          MAX_COUNT={1}
                          onFileDownload={dowloadSignatureFile}
                          onFileRemove={(file: any) => {
                            removeSignatureFile(file);
                          }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {param.tabType === tabTypeAvailability && (
                <>
                  <AvailabilitySettings
                    methods={methods}
                    param={param}
                    isLoading={isLoading}
                    durationOptions={durationOptions}
                    durationTypeOptions={durationTypeOptions}
                    locationData={locationData}
                    teamMemberLocationData={teamMemberLocationData}
                    daysOfWeek={daysOfWeek}
                    frequencyOptions={frequencyOptions}
                    filteredAvailabilityDetailColl={
                      filteredAvailabilityDetailColl
                    }
                    outreachHospitalLocationDetailColl={
                      watchedOutreachHospitalLocationDetailColl
                    }
                    handleToggleDay={handleToggleDay}
                    handleAddRow={handleAddRow}
                    handleDetailFieldChange={handleDetailFieldChange}
                    customDuration={customDuration}
                    setCustomDuration={setCustomDuration}
                    outReachLocationData={
                      outReachLocationData?.outReachLocationColl
                    }
                  />
                </>
              )}
              {param.tabType === tabTypeOutReachOPD && curTeamMemberId > 0 && (
                <>
                  <OutReachOPDList
                    hospitalData={outReachLocationData?.outReachLocationColl}
                    isLoading={isLoading}
                    value={value}
                    setValue={setValue}
                    searchValue={searchValue}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    Curr_page={Curr_page}
                    counDataHospital={counDataHospital}
                    pageStartIndexHospital={pageStartIndexHospital}
                    pageEndIndexHospital={pageEndIndexHospital}
                    totalCountHospital={totalCountHospital}
                    order={order}
                    orderBy={orderBy}
                    handleChangePageNew={handleChangePageNew}
                    curDoctorId={curTeamMemberId}
                    getOutReachLocationList={getOutReachLocationList}
                    maxOutreachOpdLocation={
                      methods.getValues()?.maxOutreachOpdLocation || 0
                    }
                  />
                </>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}
                  >
                    {curTeamMemberId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        <Dialog
          className="dialog1"
          open={customDocPopUp.open}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px", // Adjust the width as needed
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Document
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent
            //className="Investigation-Edit main-dialog-background-color"
            sx={{ padding: "12px 24px" }}
          >
            <TeamMemberDocumentSave
              handleClosePopUp={(code: string) => {
                handleManagePopup(code);
              }}
              isForPopUp={customDocPopUp.open}
              popUpteamMemberDocId={customDocPopUp?.teamMemberDocId || 0}
              popupTeamMemberId={customDocPopUp?.popupTeamMemberId || 0}
            />
          </DialogContent>
          <Divider />
        </Dialog>
      </Box>
      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setCustomDocPopUp({ ...customDocPopUp, open: true });
              }}
            >
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              className="delete-container"
              onClick={() => {
                handleDeleteDilog(true);
              }}
            >
              Delete
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this TeamMemberDocument ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(customDocPopUp.teamMemberDocId);
            }}
            className="button-delete"
          >
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
